// Step two of the CLI sign-in approval: what is being approved, and the two
// ways out of it.
//
// The device grant knowingly accepts one attack: somebody else starts a
// sign-in and talks a member into typing the code. This screen is the only
// place that can be noticed, so the address, the client and the time are the
// content here, not decoration around a confirm button. Deny sits beside
// Approve at the same size for the same reason: a member shown a code they did
// not ask for needs to kill it, not close the tab and leave it approvable.

import { Box, Flex, Stack, Text } from "@chakra-ui/react";
import { ReactNode } from "react";
import { TbAlertTriangle, TbCheck, TbX } from "react-icons/tb";

import PloyButton from "components/ds/PloyButton";
import type { CliDeviceAttempt } from "state/members/cliDeviceApproval";

import {
  countdownLabel,
  msUntil,
  requestedAgoLabel,
  requestedAtLabel,
} from "./expiry";

export type ApprovalDetailsProps = {
  attempt: CliDeviceAttempt;
  memberEmail?: string;
  /** Clock reading, passed in so the countdown ticks from the page. */
  now: number;
  /**
   * False when this browser's clock disagrees with the server's. The times
   * still show, because they came from the server and are what the member is
   * checking, but nothing is worked out from them locally.
   */
  clockTrusted: boolean;
  onApprove: () => void;
  onDeny: () => void;
  onStartOver: () => void;
  isApproving: boolean;
  isDenying: boolean;
  error: string | null;
};

const NOT_RECORDED = "Not recorded";

const Row = ({
  label,
  children,
}: {
  label: string;
  children: ReactNode;
}) => (
  <Flex
    direction={{ base: "column", sm: "row" }}
    align={{ base: "flex-start", sm: "baseline" }}
    gap={{ base: "2xs", sm: "lg" }}
    px="md"
    py="md"
    borderBottomWidth="1px"
    borderBottomColor="neutral.200"
    _last={{ borderBottomWidth: 0 }}
  >
    <Box as="dt" fontSize="xs" fontWeight={500} color="neutral.700" minW="128px">
      {label}
    </Box>
    <Box as="dd" fontSize="smd" color="neutral.950" wordBreak="break-word">
      {children}
    </Box>
  </Flex>
);

const ApprovalDetails = ({
  attempt,
  memberEmail,
  now,
  clockTrusted,
  onApprove,
  onDeny,
  onStartOver,
  isApproving,
  isDenying,
  error,
}: ApprovalDetailsProps) => {
  const busy = isApproving || isDenying;
  const left = msUntil(attempt.expiresAt, now);
  const runningOut = clockTrusted && left < 60_000;

  return (
    <Stack spacing="lg" w="full">
      <Text fontSize="smd" color="neutral.700">
        Check this against the terminal in front of you. If any of it is a
        surprise, deny it.
      </Text>

      <Box
        as="dl"
        w="full"
        bg="neutral.50"
        borderWidth="1px"
        borderColor="neutral.200"
        borderRadius="component.xl"
      >
        <Row label="Code">
          <Box as="span" fontFamily="mono" letterSpacing="0.08em">
            {attempt.userCode}
          </Box>
        </Row>
        <Row label="Asked from">
          {attempt.requestedIp ? (
            <Box as="span" fontFamily="mono">
              {attempt.requestedIp}
            </Box>
          ) : (
            <Box as="span" color="neutral.600">
              {NOT_RECORDED}
            </Box>
          )}
        </Row>
        <Row label="Asked by">
          {attempt.requestedUserAgent || (
            <Box as="span" color="neutral.600">
              {NOT_RECORDED}
            </Box>
          )}
        </Row>
        <Row label="Asked at">
          {requestedAtLabel(attempt.requestedAt)}{" "}
          {clockTrusted && (
            <Box as="span" color="neutral.700">
              ({requestedAgoLabel(attempt.requestedAt, now)})
            </Box>
          )}
        </Row>
        <Row label="Expires">
          <Box
            as="span"
            fontWeight={runningOut ? 600 : 400}
            color={runningOut ? "warning.700" : "neutral.950"}
          >
            {clockTrusted
              ? countdownLabel(left)
              : "within ten minutes of the request"}
          </Box>
        </Row>
      </Box>

      <Flex
        gap="sm"
        align="flex-start"
        bg="warning.50"
        borderWidth="1px"
        borderColor="warning.200"
        borderRadius="component.xl"
        p="md"
      >
        <Box as={TbAlertTriangle} color="warning.700" boxSize="18px" mt="2px" aria-hidden />
        <Text fontSize="smd" color="neutral.900">
          Approving signs that machine in as{" "}
          {memberEmail ? <b>{memberEmail}</b> : "you"} for up to 30 days. From
          then on it can see your access, browse catalogs and raise access
          requests in your name, without asking you again.
        </Text>
      </Flex>

      {error && (
        <Text role="alert" fontSize="smd" fontWeight={500} color="error.700">
          {error}
        </Text>
      )}

      <Flex gap="sm" direction={{ base: "column", sm: "row" }}>
        <PloyButton
          variant="primary"
          size="md"
          flex="1"
          onClick={onApprove}
          isLoading={isApproving}
          loadingText="Approving"
          isDisabled={busy}
          leftIcon={<TbCheck size={16} aria-hidden />}
        >
          Approve sign-in
        </PloyButton>
        <PloyButton
          variant="danger"
          size="md"
          flex="1"
          onClick={onDeny}
          isLoading={isDenying}
          loadingText="Denying"
          isDisabled={busy}
          leftIcon={<TbX size={16} aria-hidden />}
        >
          Deny sign-in
        </PloyButton>
      </Flex>

      <Box>
        <PloyButton
          variant="azurite-link"
          onClick={onStartOver}
          isDisabled={busy}
        >
          Type a different code
        </PloyButton>
      </Box>
    </Stack>
  );
};

export default ApprovalDetails;
